import { useState, useEffect, useMemo, useRef } from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ZAxis,
  CartesianGrid,
} from "recharts";
import {
  getTenantById,
  getTenantCompetitiveLandscapeMap,
} from "../../../../../shared/services/apis";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import saveAs from "file-saver";
import { RiDownloadLine } from "react-icons/ri";

// ---------------- Skeleton Loader ----------------
const ChartSkeleton = () => (
  <div className="w-full h-[500px] flex items-center justify-center">
    <div className="w-full h-full bg-gray-100 rounded-lg animate-pulse flex flex-col justify-between p-6">
      {/* X axis skeleton */}
      <div className="h-4 w-1/2 bg-gray-300 rounded mb-2 self-center"></div>

      {/* Chart area skeleton */}
      <div className="flex-1 flex items-center justify-center">
        <div className="grid grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="w-10 h-10 rounded-full bg-gray-300 animate-pulse"
            ></div>
          ))}
        </div>
      </div>

      {/* Y axis skeleton */}
      <div className="h-4 w-24 bg-gray-300 rounded mt-2"></div>
    </div>
  </div>
);

// ---------------- Types ----------------
export interface CompetitorMapProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}

export interface CitationData {
  citations: string[];
  search_results: SearchResult[];
  content: CompanyContent;
}

export interface SearchResult {
  title: string;
  url: string;
  date: string | null;
  last_updated: string | null;
}

export interface CompanyContent {
  company: string;
  overview: string;
  competitive_differentiators: CompetitiveDifferentiators;
  industry_environment: IndustryEnvironment;
  competitor_map: Competitor[];
  sources: string[];
}

export interface CompetitiveDifferentiators {
  regulatory_and_compliance: string;
  geographic_coverage_and_scale: string;
  technology_advantage: string;
}

export interface IndustryEnvironment {
  characteristics: string;
  trends: string[];
  concentration: IndustryLevelReason;
  barriers_to_entry: IndustryLevelReason;
  regulation_and_policy_impact: IndustryLevelReason;
  life_cycle: string;
  revenue_volatility: IndustryLevelReason;
  innovation_level: IndustryLevelReason;
}

export interface IndustryLevelReason {
  level: string;
  reason: string;
}

export interface Competitor {
  name: string;
  website: string;
  revenue: {
    amount: string;
    currency: string;
    description?: string; // optional description of revenue source
  };
  employees: number | string;
  holding_type: string;
  key_offerings: string;
  key_people: KeyPerson[];
  stock_price: string;
  recent_news: string;
  swot: SWOT;
  logo?: string; // optional logo
  fill?: string; // custom color
}

export interface KeyPerson {
  name: string;
  role: string;
  linkedin: string;
}

export interface SWOT {
  strengths: string[];
  weaknesses: string[];
  opportunities: string[];
  threats: string[];
}
interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}

// ---------------- Component ----------------
const CompetitorMap = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
}: CompetitorMapProps) => {
  const COLORS = [
    "#D3FF00",
    "#FF2A2A",
    "#63BD46",
    "#0070C0",
    "#00A1E4",
    "#4A2C1D",
    "#635bff",
    "#F79E1B",
    "#003B6D",
    "#8C52FF",
  ];

  const [data, setData] = useState<CitationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);
  const [tenant, setTenant] = useState<Tenant | null>();

  useEffect(() => {
    if (!tenantId) return;

    const fetchTenant = async () => {
      try {
        const data = await getTenantById(tenantId);
        setTenant(data);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      }
    };

    fetchTenant();
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !tenantServiceId || !tenantServiceOptionId) return;
    const fetchData = async () => {
      try {
        const res = await getTenantCompetitiveLandscapeMap(
          tenantId,
          tenantServiceId,
          tenantServiceOptionId
        );
        setData(res);
      } catch (err) {
        console.error("Failed to fetch competitor overview:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [tenantId, tenantServiceId, tenantServiceOptionId]);

  const downloadPDF = async () => {
    setDownloadingPdf(true);
    try {
      if (!chartRef.current) {
        console.error("Chart reference is null");
        alert("Chart reference is null");
        return;
      }

      const loadImage = (url: string): Promise<HTMLImageElement> =>
        new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = url;
        });

      let watermarkImg: HTMLImageElement | null = null;
      if (tenant?.logoUrl) {
        try {
          watermarkImg = await loadImage(tenant.logoUrl);
        } catch {
          // ignore watermark load errors
        }
      }

      const pageWidthPx = 750;
      const pageHeightPx = 842;
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: "#fff",
        scale: 2,
        logging: false,
        height: pageHeightPx,
        width: pageWidthPx,
        useCORS: true,
        allowTaint: true,
        foreignObjectRendering: false,
        ignoreElements: (el) =>
          el.tagName === "STYLE" || el.tagName === "SCRIPT",
        onclone: (docClone) => {
          docClone
            .querySelectorAll("style")
            .forEach((el) => (el.textContent = ""));
        },
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const margin = 15;
      const imgWidth = pageWidth - 2 * margin;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      const addWatermark = (pageNumber: number) => {
        if (!watermarkImg) return;
        pdf.setPage(pageNumber);
        pdf.saveGraphicsState();
        pdf.setGState(pdf.GState({ opacity: 0.1 }));
        const watermarkWidth = 60;
        const watermarkHeight =
          (watermarkImg.height / watermarkImg.width) * watermarkWidth;
        const xPos = (pageWidth - watermarkWidth) / 2;
        const yPos = (pageHeight - watermarkHeight) / 2;
        pdf.addImage(
          watermarkImg,
          "PNG",
          xPos,
          yPos,
          watermarkWidth,
          watermarkHeight
        );
        pdf.restoreGraphicsState();
      };

      // Title & Date BEFORE the image
      const yText = margin + 10; // adjust Y-coordinate as needed

      // Add the Title
      pdf.setFontSize(18);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor("#333333");
      pdf.text("Competitor Map Report", pageWidth / 2, yText, {
        align: "center",
      });

      // Add the Date
      pdf.setFontSize(11);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor("#666666");
      pdf.text(
        `Generated on ${new Date().toLocaleString()}`,
        pageWidth / 2,
        yText + 8,
        { align: "center" }
      );

      // Now the image
      const imageYPosition = yText + 20; // adjust spacing accordingly
      pdf.addImage(imgData, "PNG", margin, imageYPosition, imgWidth, imgHeight);
      addWatermark(1);
      // Multi-page image support
      let heightLeft = imgHeight - (pageHeight - 2 * margin);
      let currentPage = 1;

      while (heightLeft > 0) {
        pdf.addPage();
        currentPage++;
        const position = heightLeft - imgHeight;
        pdf.addImage(
          imgData,
          "PNG",
          margin,
          margin + position,
          imgWidth,
          imgHeight
        );
        addWatermark(currentPage);
        heightLeft -= pageHeight - 2 * margin;
      }

      pdf.save(
        `Competitor_Map_Report_${new Date().toISOString().slice(0, 10)}.pdf`
      );
    } catch (error) {
      console.error("PDF generation failed:", error);
      alert(
        `PDF generation failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadWord = async () => {
    setDownloadingWord(true);
    try {
      if (!chartRef.current) {
        console.error("Chart reference is null");
        alert("Chart reference is null");
        return;
      }

      // Clone the chart node and sanitize styles/classes
      const clonedElement = chartRef.current.cloneNode(true) as HTMLElement;
      clonedElement.style.position = "absolute";
      clonedElement.style.left = "-9999px";
      clonedElement.style.visibility = "visible";
      document.body.appendChild(clonedElement);

      clonedElement.querySelectorAll("style").forEach((el) => el.remove());
      clonedElement
        .querySelectorAll("*")
        .forEach((el) => (el as HTMLElement).removeAttribute("class"));

      // Sanitize all CSS style attributes of oklch colors recursively
      const sanitizeOklchColors = (element: HTMLElement) => {
        const replaceOklchRegex = (text: string): string =>
          text.replace(/oklch\([^)]*\)/g, "#000000");

        const walkElements = (el: HTMLElement) => {
          if (el.hasAttribute("style")) {
            const styleText = el.getAttribute("style") || "";
            const sanitized = replaceOklchRegex(styleText);
            if (sanitized !== styleText) el.setAttribute("style", sanitized);
          }
          if (el.style) {
            ["color", "backgroundColor", "fill"].forEach((prop) => {
              const val = el.style.getPropertyValue(prop);
              if (val && val.includes("oklch(")) {
                el.style.setProperty(prop, "#000000");
              }
            });
          }
          el.childNodes.forEach((child) => {
            if (child.nodeType === Node.ELEMENT_NODE) {
              walkElements(child as HTMLElement);
            }
          });
        };

        walkElements(element);
      };

      sanitizeOklchColors(clonedElement);

      // Load optional tenant logo for footer
      let logoImageData: {
        data: Uint8Array;
        mimeType: "jpg" | "png" | "gif" | "bmp";
      } | null = null;
      if (tenant?.logoUrl) {
        try {
          const response = await fetch(tenant.logoUrl);
          const blob = await response.blob();
          const mimeType = (() => {
            if (blob.type.includes("png")) return "png";
            if (blob.type.includes("jpeg") || blob.type.includes("jpg"))
              return "jpg";
            if (blob.type.includes("gif")) return "gif";
            if (blob.type.includes("bmp")) return "bmp";
            return "png";
          })();
          const base64Logo = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          const base64Data = base64Logo.split(",")[1];
          const bytes = new Uint8Array(
            atob(base64Data)
              .split("")
              .map((c) => c.charCodeAt(0))
          );
          logoImageData = { data: bytes, mimeType };
        } catch {
          // ignore logo load errors
        }
      }

      const pageWidthPx = 750; // jsPDF default A4 size in pixels
      const pageHeightPx = 842;
      // Capture chart as image
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        logging: false,
        height: pageHeightPx,
        width: pageWidthPx,
        useCORS: true,
        allowTaint: true,
        foreignObjectRendering: false,
        ignoreElements: (element) => {
          return element.tagName === "STYLE" || element.tagName === "SCRIPT";
        },
        onclone: (clonedDocument) => {
          clonedDocument.querySelectorAll("style").forEach((el) => {
            el.textContent = "";
          });
        },
      });

      console.log("Canvas generated:", canvas.width, "x", canvas.height);

      // Convert to base64
      const imgDataUrl = canvas.toDataURL("image/png");
      const base64String = imgDataUrl.split(",")[1];
      const imgBuffer = Uint8Array.from(atob(base64String), (c) =>
        c.charCodeAt(0)
      );

      // Import docx modules
      const {
        Document,
        Paragraph,
        ImageRun,
        Packer,
        convertInchesToTwip,
        AlignmentType,
        TextRun,
        Footer,
        HeadingLevel,
      } = await import("docx");

      // Calculate dimensions
      const pageWidth = convertInchesToTwip(8.5); // Letter page width
      const marginLeft = convertInchesToTwip(0.5);
      const marginRight = convertInchesToTwip(0.5);
      const contentWidth = pageWidth - marginLeft - marginRight;

      // Image width calculation
      const imageWidthInches = (contentWidth / 1440) * 0.9; // 90% of content width
      const canvasAspectRatio = canvas.height / canvas.width;
      const imageHeightInches = imageWidthInches * canvasAspectRatio;

      const footerChildren = [];

      if (logoImageData) {
        footerChildren.push(
          new Paragraph({
            children: [
              new ImageRun({
                type: logoImageData.mimeType,
                data: logoImageData.data,
                transformation: {
                  width: 60,
                  height: 60,
                },
              }),
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { before: 100 },
          })
        );
      }
      const doc = new Document({
        sections: [
          {
            properties: {
              page: {
                margin: {
                  top: convertInchesToTwip(0.75),
                  bottom: convertInchesToTwip(0.75),
                  left: marginLeft,
                  right: marginRight,
                },
                size: {
                  width: pageWidth,
                  height: convertInchesToTwip(11),
                },
              },
            },
            footers: {
              default: new Footer({
                children: footerChildren,
              }),
            },
            children: [
              // Title
              new Paragraph({
                text: "Growth Opportunities Report",
                heading: HeadingLevel.TITLE,
                alignment: AlignmentType.CENTER,
                spacing: { after: 300 },
              }),

              // Date
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Generated on ${new Date().toLocaleString()}`,
                    italics: true,
                    size: 20,
                    color: "666666",
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 400 },
              }),

              // Chart image - full width
              new Paragraph({
                children: [
                  new ImageRun({
                    data: imgBuffer,
                    type: "png",
                    transformation: {
                      width: Math.round(imageWidthInches * 96), // pixels
                      height: Math.round(imageHeightInches * 96), // pixels
                    },
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 400 },
              }),

              // Optional: Add a description or footer
              new Paragraph({
                children: [
                  new TextRun({
                    text: "This report contains analysis of growth opportunities.",
                    italics: true,
                    size: 20,
                    color: "888888",
                  }),
                ],
                alignment: AlignmentType.CENTER,
              }),
            ],
          },
        ],
      });

      // Generate and save document
      const blob = await Packer.toBlob(doc);
      saveAs(
        blob,
        `Competitor_Map_Report_${new Date().toISOString().slice(0, 10)}.docx`
      );
    } catch (error) {
      console.error("Word generation failed:", error);
      alert(
        `Word generation failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setDownloadingWord(false);
    }
  };

  // Format large numbers as K, M, B
  function formatNumber(num: number): string {
    if (num >= 1_000_000_000)
      return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
    if (num >= 1_000_000)
      return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
    return num.toString();
  }

  const coloredData = useMemo(() => {
    if (!data?.content?.competitor_map) return [];

    return data.content.competitor_map
      .map((item, index) => {
        // Parse employees count safely
        const employeesStr = String(item.employees ?? "");
        const employees = Number(employeesStr.replace(/[^0-9.]/g, ""));

        // Parse revenue amount safely
        const revenueAmountStr = item.revenue?.amount ?? "";
        const revenueRaw = Number(
          String(revenueAmountStr).replace(/[^0-9.]/g, "")
        );

        const revenueK = revenueRaw;

        if (isNaN(employees) || isNaN(revenueK)) return null;

        return {
          ...item,
          employees,
          revenue: revenueK,
          fill: item.fill ?? COLORS[index % COLORS.length],
        } as Competitor & { employees: number; revenue: number; fill: string };
      })
      .filter(
        (
          d
        ): d is Competitor & {
          employees: number;
          revenue: number;
          fill: string;
        } => d !== null
      );
  }, [data]);

  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: any[];
  }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div className="bg-[#0F172A] text-white px-4 py-3 rounded-md shadow-md max-w-[220px]">
          <div className="font-semibold">{d.name}</div>
          <div className="text-sm">
            {d.employees.toLocaleString()} employees · {formatNumber(d.revenue)}{" "}
            revenue
          </div>
        </div>
      );
    }
    return null;
  };

  const renderCustomDot = (props: any) => {
    const { cx, cy, payload } = props;
    const boxSize = 42;
    const logoSize = 32;
    const borderRadius = 25;

    const getInitials = (name: string) => {
      if (!name) return "";
      const parts = name.trim().split(" ");
      if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase(); // single word → first 2 letters
      }
      return (parts[0][0] + parts[1][0]).toUpperCase(); // multi word → first letters
    };
    return (
      <g cursor="pointer">
        <rect
          x={cx - boxSize / 2}
          y={cy - boxSize / 2}
          width={boxSize}
          height={boxSize}
          rx={borderRadius}
          ry={borderRadius}
          fill={payload.fill || "#F3F4F6"}
          stroke="#E5E7EB"
          strokeWidth={1}
        />
        {payload.logo ? (
          <image
            x={cx - logoSize / 2}
            y={cy - logoSize / 2}
            href={payload.logo}
            height={logoSize}
            width={logoSize}
          />
        ) : (
          <text
            x={cx}
            y={cy + 5} // adjust vertical alignment
            textAnchor="middle"
            fontSize="14"
            fontWeight="bold"
            fill="white"
          >
            {getInitials(payload.name)}
          </text>
        )}
      </g>
    );
  };

  return (
    <div className="max-w-4xl m-auto">
      <div className="flex gap-2  justify-end mb-2">
        <button
          onClick={downloadPDF}
          disabled={downloadingPdf}
          className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {downloadingPdf ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Generating...
            </>
          ) : (
            <>
              <RiDownloadLine className="w-4 h-4" /> PDF
            </>
          )}
        </button>

        <button
          onClick={downloadWord}
          disabled={downloadingWord}
          className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {downloadingWord ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Generating...
            </>
          ) : (
            <>
              <RiDownloadLine className="w-4 h-4" />
              Word
            </>
          )}
        </button>
      </div>
      <div
        ref={chartRef}
        className="rounded-2xl bg-[#FDFDFD] max-w-4xl p-6 mt-0 2xl:mt-20 m-auto  border border-gray-200 "
      >
        <h2 className="text-2xl font-light text-gray-800 mb-2">
          Competitor Map
        </h2>
        <p className="text-sm text-gray-500 mt-1 mb-4">
          Chart of competitor employee count vs revenue (in US$ )
        </p>
        <div className="w-full  cursor-pointer">
          {loading ? (
            <ChartSkeleton />
          ) : coloredData.length === 0 ? (
            <div className="text-center text-gray-500">
              No competitor data available
            </div>
          ) : (
            <div className="h-[48vh] ">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart
                  margin={{ top: 30, right: 20, bottom: 10, left: 20 }}
                >
                  <CartesianGrid stroke="#E5E7EB" />
                  <XAxis
                    type="number"
                    dataKey="employees"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(val) => val.toLocaleString()}
                    label={{
                      value: "Employee Count",
                      position: "insideBottom",
                      offset: 0,
                      fontSize: 12,
                    }}
                  />
                  <YAxis
                    type="number"
                    dataKey="revenue"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(val) => formatNumber(val)}
                    domain={[0, (dataMax: number) => dataMax + 500]}
                    label={{
                      value: "Revenue (in US$)",
                      angle: -90,
                      position: "insideLeft",
                      fontSize: 14,
                    }}
                  />
                  <ZAxis type="number" dataKey="revenue" range={[100, 300]} />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ strokeDasharray: "3 3" }}
                  />
                  <Scatter data={coloredData} shape={renderCustomDot} />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompetitorMap;
